import {KpiCalculationError} from '@hconnect/common/types'
import {customThemeConstants, getTimezoneName} from '@hconnect/uikit'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import {Box} from '@mui/material'
import moment from 'moment-timezone'
import React, {FC, useCallback, useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  MAX_VALUE,
  MIN_VALUE,
  VALIDATION_TRANSLATIONS_KEY,
  monthMapStringToDigit,
  FEATURE_NAMESPACE
} from '../consts'

type EditableMonthCellProps = {
  itemId: string
  month: string
  year: number
  value?: number
  kpiCalculationError?: KpiCalculationError
  onValueChange: (itemId: string, month: string, value?: number | KpiCalculationError) => void
  setNodeMonth: (id: string) => void
  isEditedManually?: boolean
}

export const EditableMonthCell: FC<EditableMonthCellProps> = ({
  itemId,
  month,
  year,
  value,
  kpiCalculationError,
  onValueChange,
  setNodeMonth,
  isEditedManually
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const ref = useRef<HTMLDivElement>(null)
  const timezone = getTimezoneName()

  const getErrorText = useCallback(
    (errorType: KpiCalculationError): string => t(VALIDATION_TRANSLATIONS_KEY[errorType]),
    [t]
  )
  const getDisplayValue = useCallback(
    (val?: number, error?: KpiCalculationError): string =>
      error &&
      [KpiCalculationError.EquipmentNotRunning, KpiCalculationError.EmptyKpiValue].includes(error)
        ? getErrorText(error)
        : (val?.toString() ?? ''),
    [getErrorText]
  )

  const [inputValue, setInputValue] = useState<string>(getDisplayValue(value, kpiCalculationError))
  const [internalValue, setInternalValue] = useState<number | KpiCalculationError | undefined>(
    value ?? kpiCalculationError
  )
  const [error, setError] = useState<boolean>(false)
  const [showMenu, setShowMenu] = useState(false)

  useEffect(() => {
    setInputValue(getDisplayValue(value, kpiCalculationError))
    setInternalValue(value ?? kpiCalculationError)
    setError(false)
  }, [value, kpiCalculationError, getDisplayValue])

  const notRunningText = getErrorText(KpiCalculationError.EquipmentNotRunning)
  const notApplicableText = getErrorText(KpiCalculationError.EmptyKpiValue)

  const validateValue = (val?: number): boolean =>
    val === undefined || isNaN(val) || val < MIN_VALUE || val > MAX_VALUE

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value

    if (raw === notRunningText || raw === notApplicableText) return

    setInputValue(raw)

    const val = raw === '' ? undefined : Number(raw)
    setInternalValue(val)
    setError(validateValue(val))
  }

  const applyValue = (newVal?: number | KpiCalculationError) => {
    setShowMenu(false)

    if (typeof newVal !== 'number' && newVal === internalValue) return

    if (
      newVal === KpiCalculationError.EquipmentNotRunning ||
      newVal === KpiCalculationError.EmptyKpiValue
    ) {
      setInternalValue(newVal)
      setInputValue(getDisplayValue(undefined, newVal))
      onValueChange(itemId, month, newVal)
      return
    }

    const num = newVal === undefined ? undefined : Number(newVal)
    const validationError = validateValue(num)
    if (!validationError && num !== value) {
      setInternalValue(num)
      setInputValue(num?.toString() ?? '')
      onValueChange(itemId, month, num)
    }
  }

  const handleBlur = () => {
    const isError =
      typeof internalValue !== 'number' &&
      internalValue &&
      [KpiCalculationError.EquipmentNotRunning, KpiCalculationError.EmptyKpiValue].includes(
        internalValue
      )
    if (!isError) applyValue(internalValue)
    setShowMenu(false)
  }

  const menuOptions = [
    {value: KpiCalculationError.EquipmentNotRunning, label: notRunningText},
    {value: KpiCalculationError.EmptyKpiValue, label: notApplicableText}
  ]
  const now = moment().tz(timezone)
  const selectedDate = `${year}-${monthMapStringToDigit[month.toLowerCase()]}`
  const isCurrentOrFutureMonth = moment
    .tz(selectedDate, 'YYYY-MM', timezone)
    .isSameOrAfter(now, 'month')

  if (isCurrentOrFutureMonth) return null

  return (
    <Box ref={ref} sx={{position: 'relative'}}>
      <Box sx={{position: 'relative', display: 'flex', alignItems: 'center'}}>
        <Box
          component="input"
          type="text"
          value={inputValue ?? ''}
          onClick={(e) => e.stopPropagation()}
          onFocus={() => setShowMenu(true)}
          onChange={handleChange}
          onBlur={handleBlur}
          data-test-id={`month-field-${itemId}-${month}`}
          sx={({palette}) => ({
            width: '100%',
            fontSize: 16,
            lineHeight: '24px',
            pt: 0.75,
            pb: 0.875,
            pl: 1.5,
            pr: 4,
            border: `1px solid ${error ? palette.error.main : palette.divider}`,
            borderRadius: 0.5,
            outline: 'none'
          })}
        />
        <LayersOutlinedIcon
          data-test-id={`month-field-menu-icon-${itemId}-${month}`}
          onClick={() => setNodeMonth(itemId)}
          sx={{
            fontSize: 16,
            position: 'absolute',
            right: 14,
            color: isEditedManually ? 'primary.light' : 'text.secondary',
            cursor: 'pointer',
            pointerEvents: 'auto'
          }}
        />
      </Box>

      {showMenu && (
        <Box
          sx={({palette}) => ({
            position: 'absolute',
            top: '100%',
            left: 0,
            bgcolor: palette.background.paper,
            border: `1px solid ${palette.divider}`,
            borderRadius: 0.5,
            mt: 0.25,
            zIndex: 1
          })}
        >
          {menuOptions.map(({value, label}) => (
            <Box
              key={value}
              onMouseDown={() => applyValue(value)}
              sx={{
                py: 0.75,
                px: 1,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: customThemeConstants().palette.semanticInfoFaded10
                }
              }}
              data-test-id={`month-field-option-${value}`}
            >
              {label}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}
